import ContentIllustration from '../../components/ui/ContentIllustration';
import SiteFooter from '../../components/layout/SiteFooter';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import useAuthStore from '../../store/authStore';
import publicApi from '../../services/publicApi';
import { Sparkles } from 'lucide-react';

/**
 * Build the FEATURES list dynamically so the landing always shows quotas in
 * sync with the DB (app_settings.ai_quota_subject / ai_quota_mono) and the
 * currently-active pack offers (pack_offers).
 */
function buildFeatures({ subjectCap, monoCap, packOffers }) {
    const priceFrom = packOffers?.[0]?.price_eur;
    const packLine = priceFrom
        ? `\u00c9tendez votre th\u00e9matique \u00e0 tout moment avec un Pack IA \u00e0 partir de ${Number(priceFrom).toFixed(0)} \u20ac (+${packOffers[0].doc_credits} g\u00e9n\u00e9rations IA, +${packOffers[0].mono_credits} questions IA). Les packs se cumulent sans limite.`
        : '\u00c9tendez votre th\u00e9matique \u00e0 tout moment avec un Pack IA. Les packs se cumulent sans limite.';
    return [
        { icon: 'file-text', title: 'Depuis un document', desc: 'PDF, DOCX, TXT, CSV, HTML et images. Importez un cours et l\'IA g\u00e9n\u00e8re 10 \u00e0 50 questions automatiquement.' },
        { icon: 'brain', title: 'G\u00e9n\u00e9ration IA par prompt', desc: 'D\u00e9crivez le sujet en quelques lignes, choisissez le nombre de questions — l\'IA fait le reste, sans document.' },
        { icon: 'pen-line', title: 'Cr\u00e9ation manuelle', desc: 'Cr\u00e9ez vos propres questions avec l\'\u00e9diteur int\u00e9gr\u00e9, ou enrichissez un sujet question par question avec l\'IA.' },
        { icon: 'layers', title: 'Organisation libre', desc: 'Cr\u00e9ez vos cat\u00e9gories, organisez vos sujets, personnalisez couleurs et images.' },
        { icon: 'book-open', title: '4 modes de quiz', desc: 'Quiz par sujet, examen m\u00e9lang\u00e9, progression intelligente et r\u00e9vision sur erreurs.' },
        { icon: 'bar-chart-3', title: 'Suivi de progression', desc: 'Historique complet, taux de r\u00e9ussite par sujet, forces et faiblesses, courbe de progression.' },
        { icon: 'share-2', title: 'Partage th\u00e9matique', desc: '1 code offert avec chaque abonnement. Le destinataire pourra faire vos quiz, sans modifier le contenu. Packs suppl\u00e9mentaires disponibles.' },
        { icon: 'layers', title: 'Codes cat\u00e9gorie', desc: 'Un seul code pour acc\u00e9der \u00e0 tous les sujets d\'une cat\u00e9gorie. Le destinataire n\'a pas besoin de cr\u00e9er un compte. Parfait pour un module ou un parcours th\u00e9matique.' },
        { icon: 'key-round', title: 'Codes quiz mono-sujet', desc: 'G\u00e9n\u00e9rez des codes d\'acc\u00e8s \u00e0 un quiz pr\u00e9cis. Le destinataire n\'a pas besoin de cr\u00e9er un compte. Id\u00e9al pour les formations et \u00e9valuations.' },
        { icon: 'refresh-cw', title: `Quota inclus : ${subjectCap} g\u00e9n\u00e9rations IA + ${monoCap} questions IA`, desc: `Votre abonnement inclut ${subjectCap} g\u00e9n\u00e9rations IA (cr\u00e9ation de sujet, enrichissement, document ou prompt) et ${monoCap} ajouts de questions IA par th\u00e9matique. Jusqu'\u00e0 50 questions par sujet.` },
        { icon: 'zap', title: 'Sans limite, extensible \u00e0 la demande', desc: packLine },
        { icon: 'check-circle', title: 'Disponible imm\u00e9diatement', desc: 'Les questions sont g\u00e9n\u00e9r\u00e9es en quelques secondes. Commencez \u00e0 r\u00e9viser tout de suite.' },
    ];
}

// Safe defaults used while the public settings endpoint resolves.
const FEATURES_DEFAULT = buildFeatures({ subjectCap: 30, monoCap: 100, packOffers: [] });

const STEPS = [
    { icon: 'upload', title: 'Cr\u00e9ez vos sujets', desc: 'Importez un document, d\u00e9crivez un sujet \u00e0 l\u0027IA, ou cr\u00e9ez vos questions manuellement' },
    { icon: 'brain', title: 'L\u0027IA g\u00e9n\u00e8re vos QCM', desc: 'Questions, r\u00e9ponses et explications d\u00e9taill\u00e9es \u2014 enrichissez question par question avec l\u0027IA' },
    { icon: 'target', title: 'Entra\u00eenez-vous', desc: 'Quiz adaptatif, r\u00e9vision des erreurs, suivi de progression \u2014 le m\u00eame moteur que nos th\u00e9matiques \u00e9ditoriales' },
    { icon: 'key-round', title: 'Partagez', desc: 'Codes de partage th\u00e9matique, par cat\u00e9gorie ou quiz mono-sujet \u2014 vos participants n\'ont pas besoin de cr\u00e9er un compte' },
];

const USECASES = [
    { icon: 'graduation-cap', title: '\u00c9tudiants', desc: 'Transformez vos cours en QCM pour r\u00e9viser activement plut\u00f4t que relire passivement.' },
    { icon: 'target', title: 'Candidats aux concours', desc: 'Compl\u00e9tez les th\u00e9matiques \u00e9ditoriales avec vos propres annales et fiches.' },
    { icon: 'presentation', title: 'Formateurs', desc: 'Cr\u00e9ez des quiz, g\u00e9n\u00e9rez des codes mono-sujet et suivez les r\u00e9sultats de vos apprenants sans qu\u0027ils aient \u00e0 s\u0027inscrire.' },
    { icon: 'briefcase', title: 'Professionnels', desc: 'R\u00e9glementations, proc\u00e9dures internes, certifications \u2014 r\u00e9visez sur vos documents m\u00e9tier.' },
];

export default function MaThematiqueLanding() {
    usePageTitle('Cr\u00e9ez vos QCM personnalis\u00e9s par IA');
    const user = useAuthStore(s => s.user);
    const ctaLink = user ? '/compte/quiz/mes-thematiques' : '/inscription';
    const ctaText = user ? 'Acc\u00e9der \u00e0 mes th\u00e9matiques' : 'Cr\u00e9er mon compte gratuit';

    // Features list rebuilt from DB settings (ai_quota_subject / ai_quota_mono
    // + active pack_offers) so landing numbers always match what the user gets.
    const [features, setFeatures] = useState(FEATURES_DEFAULT);
    useEffect(() => {
        let cancelled = false;
        publicApi.get('/custom/public-settings.php')
            .then(({ data }) => {
                if (cancelled || !data?.data) return;
                setFeatures(buildFeatures({
                    subjectCap: data.data.base_subject_cap,
                    monoCap:    data.data.base_mono_cap,
                    packOffers: data.data.pack_offers,
                }));
            })
            .catch(() => { /* keep defaults */ });
        return () => { cancelled = true; };
    }, []);

    return (
        <>
            {/* Hero — meme style que la section premium de la home */}
            <section className="landing-premium" style={{ paddingTop: '5rem', paddingBottom: '4rem' }}>
                <div className="section-inner" style={{ textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                        <ContentIllustration name="brain" size={56} />
                        <span className="premium-badge" style={{ fontSize: '0.85rem' }}>Premium IA</span>
                    </div>
                    <h1 style={{ fontSize: '2.4em', fontWeight: 800, color: '#fff', marginBottom: 16, fontFamily: 'var(--font-display)' }}>
                        Transformez vos documents<br />en <span style={{ color: '#c4b5fd' }}>QCM interactifs</span>
                    </h1>
                    <p style={{ fontSize: '1.15em', color: 'rgba(255,255,255,0.8)', margin: '0 auto 32px', lineHeight: 1.6 }}>
                        Importez un cours, un r&egrave;glement, une fiche de r&eacute;vision &mdash;
                        notre intelligence artificielle g&eacute;n&egrave;re automatiquement des questions
                        pour vous entra&icirc;ner.
                    </p>
                    <Link to={ctaLink} className="btn btn-premium" style={{ fontSize: '1.1em', padding: '14px 36px' }}>
                        <Sparkles size={18} /> {ctaText}
                    </Link>
                    <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85em', marginTop: 16 }}>
                        Inscription gratuite &middot; Abonnement &agrave; partir de 3&nbsp;&euro; par mois
                    </p>
                </div>
            </section>

            {/* Comment ca marche — 4 etapes */}
            <section className="landing-section">
                <div className="section-inner">
                    <h2 className="section-title">Comment &ccedil;a marche</h2>
                    <p className="section-sub">De l'import &agrave; la r&eacute;vision, en quelques secondes</p>
                    <div className="premium-block__steps" style={{ maxWidth: 600, margin: '0 auto', gap: '1.5rem' }}>
                        {STEPS.map((step, i) => (
                            <div key={i} className="premium-step" style={{ color: 'var(--text)' }}>
                                <div style={{
                                    flexShrink: 0, width: 48, height: 48, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
                                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '1.2em', fontWeight: 800
                                }}>
                                    {i + 1}
                                </div>
                                <div>
                                    <strong style={{ display: 'block', fontSize: '1.05em', marginBottom: 4, color: 'var(--heading)' }}>{step.title}</strong>
                                    <span style={{ fontSize: '0.9em', color: 'var(--text-muted)', lineHeight: 1.5 }}>{step.desc}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Fonctionnalites — grille comme premium features */}
            <section className="landing-section landing-section-alt">
                <div className="section-inner">
                    <h2 className="section-title">Tout ce dont vous avez besoin</h2>
                    <p className="section-sub">Un moteur de quiz complet pour vos contenus personnalis&eacute;s</p>
                    <div className="premium-block__features" style={{ maxWidth: 720, margin: '0 auto' }}>
                        {features.map((f, i) => (
                            <div key={i} className="premium-feature" style={{ color: 'var(--text)' }}>
                                <div className="premium-feature__icon" style={{ width: 48, height: 48, background: 'none' }}>
                                    <ContentIllustration name={f.icon} size={44} />
                                </div>
                                <div>
                                    <strong style={{ color: 'var(--heading)' }}>{f.title}</strong>
                                    <p style={{ color: 'var(--text-muted)' }}>{f.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Cas d'usage */}
            <section className="landing-section">
                <div className="section-inner">
                    <h2 className="section-title">Pour qui ?</h2>
                    <p className="section-sub">Quel que soit votre objectif, cr&eacute;ez des QCM adapt&eacute;s</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, maxWidth: 800, margin: '0 auto' }}>
                        {USECASES.map((u, i) => (
                            <div key={i} style={{
                                textAlign: 'center', padding: '28px 20px',
                                background: 'var(--card-bg)', borderRadius: 16,
                                border: '1px solid var(--card-border)',
                                boxShadow: '0 1px 4px var(--card-shadow)'
                            }}>
                                <div style={{
                                    width: 52, height: 52, borderRadius: '50%',
                                    background: 'none',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto 14px'
                                }}>
                                    <ContentIllustration name={u.icon} size={52} />
                                </div>
                                <h3 style={{ fontSize: '1.05em', marginBottom: 8, color: 'var(--heading)' }}>{u.title}</h3>
                                <p style={{ fontSize: '0.9em', color: 'var(--text-muted)', lineHeight: 1.5 }}>{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA final — meme style premium */}
            <section className="landing-premium" style={{ paddingTop: '3.5rem', paddingBottom: '3.5rem' }}>
                <div className="section-inner" style={{ textAlign: 'center' }}>
                    <h2 style={{ fontSize: '1.6em', color: '#fff', marginBottom: 12, fontFamily: 'var(--font-display)', fontWeight: 800 }}>
                        Pr&ecirc;t &agrave; cr&eacute;er vos propres QCM ?
                    </h2>
                    <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 28, fontSize: '1.05em' }}>
                        Inscription gratuite &middot; Abonnement &agrave; partir de 3&nbsp;&euro; par mois
                    </p>
                    <Link to={ctaLink} className="btn btn-premium" style={{ fontSize: '1.1em', padding: '14px 36px' }}>
                        <Sparkles size={18} /> {ctaText}
                    </Link>
                </div>
            </section>
            <SiteFooter />
        </>
    );
}
